import { SelectionMenuViewMode } from '@components/ButtonBaseWithSelectionMenu/types/buttonBaseWithSelectionMenu.types';
import Checkbox from '@components/v2/form/Checkbox/Checkbox';
import Modal from '@components/v2/ui/Modal/Modal';
import Typography from '@components/v2/ui/Typography/Typography';
import { Stack } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import usePath from '../../../../../../components/Hooks/usePath';
import useWidth from '../../../../../../components/Hooks/useWidth';
import LangEnum from '../../../../../../lang/LangEnum';
import PATHS from '../../../../../../routes/routes.const';
import { getOrderProductUniqueIdentifier } from '../../../../../../utils/getOrderProductUniqueIdentifier';
import { OrderFormTestIds } from '../../../../../../utils/testIds/OrderForm.testIds';
import { OrderProductsTestIds } from '../../../../../../utils/testIds/OrderProducts.testIds';
import { useApplicationContext } from '../../../../../context/ApplicationContext/ApplicationContext';
import { useProductLock } from '../../../../../products/products-details-v2/hooks/useProductLock';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { useOrderContext } from '../../../../contexts/Order.context';
import { useOrderProductsContext } from '../../../../contexts/OrderProducts.context';
import { useOrderProductsLayoutContext } from '../../../../contexts/OrderProductsLayout.context';
import useOrderProductGridStyles from '../../../../styles/useOrderProductGrid.styles';
import { useOrderProductItemStyles } from '../../../../styles/useOrderProductItemStyles';
import { useGrid } from '../../../hooks/useGrid';
import { UpdateItemFormat } from '../../../orderProducts.types';
import { OrderProductRowLayout } from '../../../orderProductsLayout.types';
import { isOrderProductRowInline } from '../../../orderProductsLayout.utils';
import OrderProductActions from '../../components/OrderProductActions/OrderProductActions';
import OrderProductColor from '../../components/OrderProductColor';
import OrderProductDeliveryWindow from '../../components/OrderProductDeliveryWindow';
import OrderProductImageAndLabel from '../../components/OrderProductImageAndLabel/OrderProductImageAndLabel';
import OrderProductStatus from '../../components/OrderProductStatus';
import OrderProductTotals from '../../components/OrderProductTotals';
import OrderProductGridContainer from '../../grid/components/orderProductGrid/components/grid/components/OrderProductGridContainer';
import { OrderProductForGridContainerTransformer } from '../../grid/components/orderProductGrid/components/grid/types/orderProductGridContainer.types';
import { useGridStoreApiOptional } from '../../grid/store/GridStoreProvider';
import { produceItemsToStore } from '../../grid/store/produceItemsToStore';
import { useIsCombinationPending } from '../../grid/store/useIsCombinationPending';
import OrderProductItemSkeleton from '../OrderProductItemSkeleton/OrderProductItemSkeleton';
import useOrderProductItem from '../useOrderProductItem';
import { OrderProductItemProps } from './OrderProductItem.interfaces';

const resolveRowCheckboxSize = (isBelowDesktop: boolean) => (isBelowDesktop ? 'medium' : 'small');

const OrderProductItem = ({
  sections,
  sectionIndex,
  isLastSection,
  product,
  productIndex,
  canEditOrder,
  orderStatus,
  orderId,
  viewMode,
  orderBrandHasIntegrations,
  productErrors,
  isSelected,
  selectionEdge = 'none',
  toggleItems,
  removeItems,
}: OrderProductItemProps) => {
  const { getLimit, getStockValue } = useProductLock();
  const { layout, hasTouchGrid } = useOrderProductsLayoutContext();
  const isMobileLayout = layout === OrderProductRowLayout.MOBILE;
  const isInlineLayout = isOrderProductRowInline(layout);
  const { hasIntegrations } = useApplicationContext();
  const { removeCopiedGrid, copiedGrid, copyGrid } = useOrderContext();
  const { products } = useOrderProductsContext();
  const { isPath } = usePath();
  const { mediumDownWidth } = useWidth();
  const { translate } = useTranslation();
  const gridStoreApi = useGridStoreApiOptional();

  const {
    grid,
    total,
    combinationKey,
    partsQuantity,
    hasValidPacks,
    suggestedGrid,
    suggestedPacks,
    selectedPacks,
    isLoadingGrid,
    isFetchingGrid,
    showSuggestedGrid,
    suggestionAchieved,
    handleClickPaste,
    resetSelectedPacks,
    handleSelectSuggestion,
  } = useGrid({ product, orderId });

  const isLoading = useIsCombinationPending(combinationKey);

  const {
    handleClick,
    packsWithError,
    selloutSummary,
    highlightProduct,
    handleClickRemove: onRemove,
    orderProductKeyIds,
    isLastOrderProduct,
    handleSelectNewColor,
    productAddedByActions,
    isDeletingKit,
    isOrderProductFromKit,
    closeConfirmRemoveModal,
    handleRemoveKitFromOrder,
    isConfirmRemoveModalOpen,
    packsWithUnavailableStock,
    isOfflineOrderInOnlineMode,
    isNotAbandonedOrCanceledOrder,
    handleSelectNewDeliveryWindow,
  } = useOrderProductItem({
    grid,
    product,
    sections,
    productIndex,
    sectionIndex,
    isLastSection,
    productErrors,
  });

  const { classes, cx } = useOrderProductItemStyles({
    isLastOrderProduct,
    highlightProduct,
    layout,
    hasTouchGrid,
    selectionEdge,
  });

  const { classes: orderProductGridClasses } = useOrderProductGridStyles({ hasTouchGrid });

  const showProductActions =
    (viewMode === SelectionMenuViewMode.EDIT && orderStatus !== 'SUGGESTED') || canEditOrder;
  const productSizeNames = useMemo(() => grid.map((gridItem) => gridItem.productSizeName), [grid]);

  const stackOrderItem = useCallback(
    (item: UpdateItemFormat) => {
      if (!gridStoreApi) {
        return;
      }

      produceItemsToStore({
        api: gridStoreApi,
        combinationKey,
        items: [{ ...item, productColorId: product.productColorId, orderId }],
      });
    },
    [gridStoreApi, combinationKey, product.productColorId, orderId],
  );

  const orderProductUniqueIdentifier = getOrderProductUniqueIdentifier(product);

  const handleKeys = useCallback(() => {
    const keys = new Set([orderProductUniqueIdentifier]);
    if (product.kit?.id) {
      products.forEach((item) => {
        if (item.kit?.id === product.kit?.id) {
          keys.add(getOrderProductUniqueIdentifier(item));
        }
      });
    }

    return keys;
  }, [orderProductUniqueIdentifier, products, product]);

  const handleClickRemove = useCallback(() => {
    const keys = handleKeys();

    removeItems(keys);
    onRemove(product);
  }, [removeItems, onRemove, product, handleKeys]);

  const handleToggleItem = useCallback(() => {
    const keys = handleKeys();

    toggleItems(keys);
  }, [handleKeys, toggleItems]);
  if (isLoadingGrid && isFetchingGrid) {
    return (
      <OrderProductItemSkeleton
        canEditOrder={canEditOrder}
        selectionEdge={selectionEdge}
        isLastOrderProduct={isLastOrderProduct}
      />
    );
  }

  const isFinishedOrder = viewMode === SelectionMenuViewMode.VIEW;
  const disableOrderProductButtons =
    isFinishedOrder || isLoadingGrid || !canEditOrder || isOrderProductFromKit;

  const showProductStatus =
    isFinishedOrder &&
    isNotAbandonedOrCanceledOrder &&
    (hasIntegrations || orderBrandHasIntegrations);

  const orderProductStatus = showProductStatus ? <OrderProductStatus grid={grid} /> : null;

  const orderProductActions = (
    <OrderProductActions
      handleClickRemove={handleClickRemove}
      hasTouchLayout={mediumDownWidth}
      removeActionCustomLabel={
        isOrderProductFromKit ? LangEnum.GLOBAL_REMOVE_PRODUCTS_BUNDLE : undefined
      }
    />
  );

  return (
    <>
      <Stack
        className={cx(classes.root, classes.fixedRowHeight, orderProductGridClasses.root, {
          [classes.selectedRow]: isSelected,
        })}
        direction="row"
        test-id={OrderFormTestIds.ORDER_PRODUCTS_WRAPPER}
        sx={{
          alignContent: 'flex-start',
          flexWrap: isInlineLayout ? 'nowrap' : 'wrap',
          alignItems: isMobileLayout ? 'flex-start' : 'center',
        }}
      >
        <div className={classes.orderProductInfoContainer}>
          <Stack
            className={classes.orderProductInfo}
            sx={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: isMobileLayout ? 'wrap' : 'nowrap',
            }}
          >
            <div className={classes.orderProductImageAndLabel}>
              {canEditOrder && (
                <Checkbox
                  size={resolveRowCheckboxSize(mediumDownWidth)}
                  checked={isSelected}
                  disabled={disableOrderProductButtons}
                  className={classes.checkbox}
                  onClick={handleToggleItem}
                />
              )}
              <OrderProductImageAndLabel
                media={product.media}
                name={product.name}
                code={product.code}
                referenceCode={product.referenceCode}
                productErrors={productErrors}
                viewMode={viewMode}
                classifications={product.colorClassifications}
                orderProductKeyIds={orderProductKeyIds}
                onClick={handleClick}
                suggestedGrid={suggestedGrid}
                showSuggestedGrid={showSuggestedGrid}
                clickable={!isOfflineOrderInOnlineMode}
                selloutSummary={selloutSummary}
              />
            </div>

            {isMobileLayout && (
              <div className={classes.orderProductMobileTotals}>
                <OrderProductTotals
                  grid={grid}
                  total={total}
                  partsQuantity={partsQuantity}
                  combinationKey={combinationKey}
                  isFinishedOrder={isFinishedOrder}
                />
                {showProductActions && orderProductActions}
                {orderProductStatus}
              </div>
            )}

            <div className={classes.orderProductButtons}>
              <div className={classes.orderProductColor}>
                <OrderProductColor
                  viewMode={viewMode}
                  currentColorId={product.productColorId}
                  color={product.color}
                  productId={product.productId}
                  isLoadingButton={isLoading}
                  onSelectNewColor={handleSelectNewColor}
                  disabled={disableOrderProductButtons}
                />
              </div>

              <div className={classes.orderProductDeliveryWindow}>
                <OrderProductDeliveryWindow
                  canEditOrder={canEditOrder}
                  deliveryWindow={product.deliveryWindow}
                  onSelectNewDeliveryWindow={handleSelectNewDeliveryWindow}
                  productId={product.productId}
                  productColorId={product.productColorId}
                  availability={product.availability}
                  viewMode={viewMode}
                  isLoadingButton={isLoading || isLoadingGrid}
                  disabled={disableOrderProductButtons}
                />
              </div>
            </div>
          </Stack>
        </div>

        <div
          className={classes.orderProductGridContainer}
          test-id={`${OrderProductsTestIds.GRID_LINE}-${product.color?.code ?? 'none'}-${product.availability}`}
        >
          <OrderProductGridContainer
            copiedGrid={copiedGrid}
            copyGrid={copyGrid}
            getLimit={getLimit}
            getStockValue={getStockValue}
            grid={grid}
            handleClickPaste={handleClickPaste}
            handleSelectSuggestion={handleSelectSuggestion}
            hasValidPacks={hasValidPacks}
            highlightProduct={highlightProduct}
            orderId={orderId}
            orderStatus={orderStatus}
            packsWithError={packsWithError}
            packsWithUnavailableStock={packsWithUnavailableStock}
            product={{
              ...OrderProductForGridContainerTransformer.fromOrder(product),
              combinationKey,
            }}
            productAddedByActions={productAddedByActions}
            productSizeNames={productSizeNames}
            readMode={!isPath(PATHS.main.orders.editForm.path) || !canEditOrder}
            removeCopiedGrid={removeCopiedGrid}
            resetSelectedPacks={resetSelectedPacks}
            selectedPacks={selectedPacks}
            showSuggestedGrid={viewMode !== 'VIEW' && showSuggestedGrid}
            stackOrderItem={stackOrderItem}
            suggestedGrid={suggestedGrid}
            suggestedPacks={suggestedPacks}
            suggestionAchieved={suggestionAchieved}
            selloutSummary={selloutSummary}
          />
        </div>

        <div className={classes.orderProductTotalsAndActionsWrapper}>
          <div className={classes.orderProductTotalsAndActionsContainer}>
            {!isMobileLayout && (
              <OrderProductTotals
                grid={grid}
                total={total}
                partsQuantity={partsQuantity}
                combinationKey={combinationKey}
                isFinishedOrder={isFinishedOrder}
              />
            )}
            {!isMobileLayout && showProductActions && orderProductActions}
            {!isMobileLayout && orderProductStatus}
          </div>
        </div>
      </Stack>
      {!!product.kit && !!product.kit.id && (
        <Modal
          isOpen={isConfirmRemoveModalOpen}
          size="standard"
          header={{ title: translate(LangEnum.REMOVE_PRODUCTS_BUNDLE_CONFIRM_MODAL_TITLE) }}
          onClose={closeConfirmRemoveModal}
          footer={{
            primaryAction: {
              label: translate(LangEnum.GLOBAL_REMOVE),
              onClick: () => handleRemoveKitFromOrder(),
              isLoading: isDeletingKit,
              isDisabled: isDeletingKit,
            },
            secondaryAction: {
              label: translate(LangEnum.DELETE_POPPER_CANCEL),
              onClick: closeConfirmRemoveModal,
              isDisabled: isDeletingKit,
            },
          }}
        >
          <Typography color="textSecondary" sx={{ fontSize: '14px' }}>
            {translate(LangEnum.REMOVE_PRODUCTS_BUNDLE_CONFIRM_MODAL_DESCRIPTION)}
          </Typography>
        </Modal>
      )}
    </>
  );
};

export default memo(OrderProductItem);
